import { SupplierGapDocumentUploadDetails } from '../../../store/Models/Api/DataCollectionGapSummaryModels';

export const SupplierGapDocumentSubmissionPath = '/Surveys/SubmitGapDocuments';

export interface SupplierGapDocumentSubmissionDetails {
    customerName: string | null;
    surveyName: string | null;
    isSubmitted: boolean;
    submittedAtUtc: string | null;
}

export type SupplierGapDocumentSubmissionResult =
    | { status: 'ok'; data: SupplierGapDocumentSubmissionDetails }
    | { status: 'unavailable' | 'invalid-files' | 'failed'; data: null };

export type SupplierGapDocumentUploadResult =
    | { status: 'ok'; data: SupplierGapDocumentUploadDetails }
    | { status: 'unavailable' | 'invalid-files' | 'failed'; data: null };

interface ApiDataResponse<T> {
    result: string;
    data: T | null;
}

const parseResponse = async (response: Response): Promise<SupplierGapDocumentSubmissionResult> => {
    if (!response.ok) {
        return { status: 'failed', data: null };
    }

    const body = await response.json() as ApiDataResponse<SupplierGapDocumentSubmissionDetails>;

    if (body.result === 'Ok' && body.data !== null) {
        return { status: 'ok', data: body.data };
    }

    if (body.result === 'RequestNotAvailable') {
        return { status: 'unavailable', data: null };
    }

    if (body.result === 'InvalidFiles') {
        return { status: 'invalid-files', data: null };
    }

    return { status: 'failed', data: null };
};

export const getSupplierGapDocumentSubmission = async (
    accessCode: string
): Promise<SupplierGapDocumentSubmissionResult> => {
    try {
        const response = await fetch(
            `/Api/v2/GetSupplierGapDocumentSubmission?accessCode=${encodeURIComponent(accessCode)}`,
            { method: 'GET', credentials: 'same-origin', cache: 'no-store' }
        );

        return await parseResponse(response);
    } catch {
        return { status: 'failed', data: null };
    }
};

export const uploadSupplierGapDocument = async (
    accessCode: string,
    file: File
): Promise<SupplierGapDocumentUploadResult> => {
    const formData = new FormData();
    formData.append('accessCode', accessCode);
    formData.append('file', file, file.name);

    try {
        const response = await fetch('/Api/v2/UploadSupplierGapDocument', {
            method: 'POST',
            credentials: 'same-origin',
            body: formData
        });

        if (!response.ok) {
            return { status: 'failed', data: null };
        }

        const body = await response.json() as ApiDataResponse<SupplierGapDocumentUploadDetails>;
        if (body.result === 'Ok' && body.data !== null) {
            return { status: 'ok', data: body.data };
        }

        if (body.result === 'RequestNotAvailable') {
            return { status: 'unavailable', data: null };
        }

        if (body.result === 'InvalidFiles') {
            return { status: 'invalid-files', data: null };
        }

        return { status: 'failed', data: null };
    } catch {
        return { status: 'failed', data: null };
    }
};

export const submitSupplierGapDocuments = async (
    accessCode: string,
    fileIds: string[]
): Promise<SupplierGapDocumentSubmissionResult> => {
    try {
        const response = await fetch('/Api/v2/SubmitSupplierGapDocuments', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ accessCode, fileIds })
        });

        return await parseResponse(response);
    } catch {
        return { status: 'failed', data: null };
    }
};
